/** Partner (kurum) üyeliği: KarteX API /v2/Partners. Panel yalnız çağıranın yönettiği partnerleri görür. */

/** GET /v2/Partners/Owned öğesi. documentIds: üyelerin katılırken sırayla onayladığı belgeler (Onay belgeleri listesinden, 0..5). */
export interface OwnedPartner {
    id: string;
    name: string;
    membershipType: string | null;
    documentIds: string[];
    /** Yalnız aktif (onaylanmış) üye sayısı. */
    memberCount: number;
    /** Onay bekleyen başvuru sayısı; eski sunucuda yoksa 0 sayılır. */
    pendingCount?: number;
    /** Üye no öneki; numara "{önek}-000001", önek yoksa "000001" biçimindedir. */
    memberNoPrefix: string | null;
    /** Org yüzeyi (GET /v2/Org/Membership): son verilen üye sırası ve bir sonraki onayda verilecek numara (= son + 1). */
    lastMemberSeq?: number;
    nextMemberNo?: number;
    /** KarteX onayı: false ise kurum mobilde listelenmez (bilgi notu). Eksikse (eski sunucu) not gösterilmez. */
    verified?: boolean;
    /** Hesap türü (sunucu Partner.Kind; sayı gelirse 0 = Membership, 1 = Company). Eski sunucuda yoksa tür bilinmez;
     * AccountModeService oturuma göre yorumlar (bkz. ownedPartnerKind). */
    kind?: OwnedPartnerKind | 0 | 1;
}

/** Owned satırının türü: Üyelik (Organizasyon/Partner) ya da Şirket (QR kurulumunda açılan şirket kaydı). */
export type OwnedPartnerKind = 'Membership' | 'Company';

/** Owned satırının türü; alan yoksa ya da tanınmıyorsa null (tür bilinmiyor). */
export function ownedPartnerKind(p: OwnedPartner): OwnedPartnerKind | null {
    const k = p.kind;
    if (k === 0 || (typeof k === 'string' && k.toLowerCase() === 'membership')) return 'Membership';
    if (k === 1 || (typeof k === 'string' && k.toLowerCase() === 'company')) return 'Company';
    return null;
}

/** PUT /v2/Partners/{id}/Documents gövdesi (başarıda 204). Sıra = dizideki sıra; tekrarsız, en çok PARTNER_DOCUMENTS_MAX. */
export interface PartnerDocumentsRequest {
    documentIds: string[];
}

/** Bir kurumdaki en çok onay belgesi sayısı (sunucu kuralıyla aynı). */
export const PARTNER_DOCUMENTS_MAX = 5;

/** PUT /v2/Partners/{id} gövdesi: kurum bilgisi. */
export interface PartnerInfoRequest {
    name: string;
    membershipType: string | null;
    /** null: değişmez; "": temizlenir. */
    memberNoPrefix: string | null;
}

/** Kurum bilgisi sınırları (sunucu kuralıyla aynı). */
export const PARTNER_NAME_MAX = 100;
export const PARTNER_MEMBERSHIP_TYPE_MAX = 40;
export const PARTNER_MEMBER_NO_PREFIX_MAX = 10;

/** Üye no öneki: en çok 10 karakter; harf (Türkçe dahil), rakam ve '-'. Boş = önek yok. */
export function isValidMemberNoPrefix(value: string): boolean {
    const t = value.trim();
    return t.length === 0 || (t.length <= PARTNER_MEMBER_NO_PREFIX_MAX && /^[\p{L}\p{N}-]+$/u.test(t));
}

/** Üyenin onayladığı bir belge: ad ve onaylanan sürüm numarası. */
export interface PartnerMemberConsent {
    documentId: string;
    name: string;
    version: number;
}

/** GET /v2/Partners/{id}/Members öğesi. consents: üyenin onayladığı belgeler (kurumun belge sırasında). */
export interface PartnerMember {
    id: string;
    partnerId: string;
    partnerName: string;
    fullName: string;
    profession: string | null;
    membershipType: string | null;
    /** Üye no onayla birlikte sunucuda verilir; bekleyen başvuruda null. */
    memberNo: string | null;
    joinedAt: string;
    /** Eksikse (eski sunucu) Active sayılır; bkz. memberStatus. */
    status?: PartnerMemberStatus | null;
    approvedDate?: string | null;
    /** Sonlanma zamanı (ISO); yalnız Ended satırda dolu. */
    endedDate?: string | null;
    /** Kim sonlandırdı: üye ayrıldı ya da kurum sonlandırdı; yalnız Ended satırda dolu. */
    endedBy?: PartnerMemberEndedBy | null;
    /** Üye sınırı nedeniyle bekletildiği an (ISO); bekletilmiyorsa null/yok. */
    capacityHoldSince?: string | null;
    consents: PartnerMemberConsent[];
}

/** Üyelik durumu: mobilde "Üye ol" başvuruyu Pending kaydeder; kurum yöneticisi panelde onaylar.
 * Ended: üye ayrıldı ya da kurum sonlandırdı (satır listede kalır, kişi yeniden başvurabilir). */
export type PartnerMemberStatus = 'Pending' | 'Active' | 'Ended';

/** Üyeliği kim sonlandırdı. */
export type PartnerMemberEndedBy = 'Member' | 'Partner';

/** Eksik ya da tanınmayan durum Active sayılır (eski sunucu sözleşmesi). */
export function memberStatus(m: Pick<PartnerMember, 'status'>): PartnerMemberStatus {
    return m.status === 'Pending' || m.status === 'Ended' ? m.status : 'Active';
}

/** POST /v2/Partners/{id}/Members/Approve gövdesi; bir istekte 1..100 kimlik. */
export interface ApproveMembersRequest {
    membershipIds: string[];
}

/** POST /v2/Partners/{id}/Members/Approve yanıtı; skipped: zaten aktif ya da bulunamayan kayıt sayısı. */
export interface ApproveMembersResponse {
    approved: PartnerMember[];
    skipped: number;
}

/** Bir onay isteğindeki en çok kimlik sayısı (sunucu kuralıyla aynı). */
export const APPROVE_BATCH_MAX = 100;

/** Sunucunun seçilen belge şirkete ait değilse ya da yoksa döndüğü 400 kodu. */
export const INVALID_DOCUMENT_CODE = 'InvalidDocument';

/** GET /v2/Org/Capacity ve POST /v2/Org/CapacityRequest yanıtı (API ile birebir, camelCase). */
export interface OrgCapacityView {
    kind: 'company' | 'membership';
    limit: number | null; // null = sınırsız
    used: number;
    held: number; // sınır nedeniyle bekleyen başvuru
    openRequest: boolean;
    requestedAt: string | null;
    /** Son karara bağlanan talebin sonucu (açık talep yokken; süre sınırı yok, kullanıcı kapatır). Eski sunucuda gelmez. */
    lastDecision?: 'Approved' | 'Rejected' | null;
    lastDecidedAt?: string | null;
    /** Yalnız Approved: kuyruğa bırakılan (onay bekleyen) başvuru sayısı. */
    releasedToQueue?: number | null;
    /** Panel bu kimliği tarayıcıda saklayıp bandı bir daha göstermez. */
    lastDecisionId?: string | null;
}
/** API TransitionResult; panel yalnız sayısını kullanır. */
export type TransitionResult = Readonly<Record<string, unknown>>;
/** POST /v2/Org/Memberships/ApproveBatch yanıtı. */
export interface ApproveBatchResult {
    approved: TransitionResult[];
    held: number;
    limit: number | null;
    skipped: number;
}
